import { createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'
import {
  getGame,
  updateGame,
  getGameFormats,
  deleteGame,
  deleteGamePlayer,
  getGameLogs,
  deleteGameRound,
} from './thunks'
import { LOGS_REQUEST, parseLogsResults } from 'utils/logs'

const initialState: GameState = {
  game: null,
  gameFormats: [],
  status: 'idle',
  error: null,
  updatingStatus: 'idle',
  updatingError: null,
  deletingStatus: 'idle',
  deletingError: null,
  gameFormatsStatus: 'idle',
  gameFormatsError: null,
  logsStatus: 'idle',
  logsError: null,
  deletingId: null,
  updatingId: null,
}

const slice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    clearGame: (state) => {
      state.game = null
    },
    clearGameLogs: (state) => {
      state.gameLogs = undefined
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGame.pending, (state) => {
        state.status = 'loading'
        state.error = null
        state.updatingError = null
        state.updatingStatus = 'idle'
      })
      .addCase(getGame.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getGame.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.game = action.payload
      })

    builder
      .addCase(updateGame.pending, (state, action) => {
        state.updatingStatus = 'loading'
        state.updatingError = null
        const { id } = action.meta.arg
        state.updatingId = typeof id === 'string' ? parseInt(id) : id
      })
      .addCase(updateGame.rejected, (state, action) => {
        state.updatingStatus = 'failed'
        state.updatingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(updateGame.fulfilled, (state, action) => {
        state.updatingStatus = 'succeeded'
        state.game = action.payload
        state.updatingId = null
      })

    builder
      .addCase(deleteGame.pending, (state, action) => {
        state.deletingStatus = 'loading'
        state.deletingError = null
        state.deletingId = typeof action.meta.arg === 'string' ? parseInt(action.meta.arg) : action.meta.arg
      })
      .addCase(deleteGame.rejected, (state, action) => {
        state.deletingStatus = 'failed'
        state.deletingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(deleteGame.fulfilled, (state) => {
        state.deletingStatus = 'succeeded'
        state.deletingId = null
      })

    builder
      .addCase(deleteGameRound.pending, (state, action) => {
        state.deletingStatus = 'loading'
        state.deletingError = null
        state.deletingId = action.meta.arg.roundId
      })
      .addCase(deleteGameRound.rejected, (state, action) => {
        state.deletingStatus = 'failed'
        state.deletingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(deleteGameRound.fulfilled, (state) => {
        state.deletingStatus = 'succeeded'
        state.deletingId = null
      })

    builder
      .addCase(deleteGamePlayer.pending, (state, action) => {
        state.deletingStatus = 'loading'
        state.deletingError = null
        state.deletingId = action.meta.arg.playerId
      })
      .addCase(deleteGamePlayer.rejected, (state, action) => {
        state.deletingStatus = 'failed'
        state.deletingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(deleteGamePlayer.fulfilled, (state) => {
        state.deletingStatus = 'succeeded'
        state.deletingId = null
      })

    builder
      .addCase(getGameFormats.pending, (state) => {
        state.gameFormatsStatus = 'loading'
        state.gameFormatsError = null
      })
      .addCase(getGameFormats.rejected, (state, action) => {
        state.gameFormatsStatus = 'failed'
        state.gameFormatsError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getGameFormats.fulfilled, (state, action) => {
        state.gameFormatsStatus = 'succeeded'
        state.gameFormats = action.payload
      })

    builder
      .addCase(getGameLogs.pending, (state) => {
        state.logsStatus = 'loading'
        state.logsError = null
      })
      .addCase(getGameLogs.rejected, (state, action) => {
        state.logsStatus = 'failed'
        state.logsError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getGameLogs.fulfilled, (state, action) => {
        const data: LogsResponse = action.payload
        const isCompleted = data.status === LOGS_REQUEST.Complete
        const isRunning = data.status === LOGS_REQUEST.Running
        if (isCompleted || isRunning) {
          const logEvents = parseLogsResults(data.results)
          state.gameLogs = logEvents
        }
        if (isCompleted) {
          state.logsStatus = 'succeeded'
        }
      })
  },
})

export const { clearGame, clearGameLogs } = slice.actions

export const selectGame = (state: RootState): GameState => state.game

export default slice.reducer
